import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight, Sparkles, Building2, Leaf, Share2 } from "lucide-react";
import SEO from "@/components/SEO";
import Hero from "@/components/sections/Hero";
import FormationsGrid from "@/components/sections/FormationsGrid";
import TrustSignals from "@/components/sections/TrustSignals";
import Testimonials from "@/components/sections/Testimonials";
import FAQ from "@/components/sections/FAQ";
import CTASection from "@/components/sections/CTASection";
import { blogPosts } from "@/data/blog";

export default function Home() {
  const orgSchema = {
    "@context": "https://schema.org",
    "@type": "EducationalOrganization",
    "name": "YAFORMIS",
    "url": "https://www.gme-assistance.fr",
    "logo": "https://www.gme-assistance.fr/logo-yaformis.png",
    "description": "Organisme de formation professionnelle spécialisé en intelligence artificielle.",
    "email": "yaformis@gmail.com",
    "telephone": "+33626345597",
    "address": { "@type": "PostalAddress", "addressCountry": "FR" },
    "sameAs": []
  };

  return (
    <>
      <SEO
        title="Formation Intelligence Artificielle — Certifiante CPF / OPCO"
        description="YAFORMIS — Organisme spécialisé en formation intelligence artificielle pour entreprises et salariés. Formation IA certifiante CPF, OPCO, Qualiopi. Initiation IA, outils IA, IA générative, agents IA. Présentiel et distanciel partout en France."
        path="/"
        schema={orgSchema}
      />
      <Hero />
      <FormationsGrid />
      <ProcessSection />
      <TrustSignals />
      <Testimonials />
      <SisterSiteSection />
      <BlogPreview />
      <FAQ />
      <CTASection />
    </>
  );
}

function ProcessSection() {
  const steps = [
    { num: "01", title: "Audit de votre projet", desc: "Échange de 30 minutes avec un conseiller pédagogique pour comprendre vos enjeux." },
    { num: "02", title: "Programme personnalisé", desc: "Devis et programme adapté à votre métier, vos outils et votre niveau." },
    { num: "03", title: "Formation pratique", desc: "Présentiel ou distanciel. 60% de pratique sur vos cas réels." },
    { num: "04", title: "Suivi 6 mois", desc: "Coaching, ressources actualisées, mentorat technique selon programme." },
  ];
  return (
    <section className="bg-brand-page py-14 md:py-20" data-testid="process-section">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end mb-10">
          <div className="lg:col-span-7">
            <div className="overline">Notre méthode</div>
            <h2 className="font-heading font-black tracking-tighter text-3xl sm:text-4xl lg:text-5xl mt-3 leading-[1.05] text-brand-ink">
              De l'audit au déploiement en entreprise.
            </h2>
          </div>
          <div className="lg:col-span-5 text-slate-600 text-base md:text-lg leading-relaxed">
            Une approche en quatre étapes pour garantir un transfert de compétences réel et durable, applicable dès le lendemain de la formation.
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-brand-line border border-brand-line">
          {steps.map((s) => (
            <div key={s.num} className="bg-white p-7 md:p-8 hover:bg-brand-surface transition-colors" data-testid={`process-step-${s.num}`}>
              <div className="num-marker">{s.num} / 04</div>
              <h3 className="font-heading font-bold text-xl mt-5 text-brand-ink">{s.title}</h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function SisterSiteSection() {
  const expertises = [
    {
      icon: Building2,
      title: "Marchés publics",
      desc: "Accompagnement à la réponse aux appels d'offres, rédaction de mémoires techniques, veille et stratégie commerciale.",
    },
    {
      icon: Leaf,
      title: "RSE",
      desc: "Conseil et déploiement de démarches responsabilité sociétale : reporting, labellisation, accompagnement au changement.",
    },
    {
      icon: Share2,
      title: "Réseaux sociaux",
      desc: "Stratégie social media, création de contenus, animation de communautés professionnelles et relations influenceurs.",
    },
  ];
  return (
    <section className="bg-white py-14 md:py-20 border-y border-brand-line" data-testid="sister-site-section">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5">
            <div className="overline">Également disponible · Autre expertise</div>
            <h2 className="font-heading font-black tracking-tighter text-3xl sm:text-4xl lg:text-5xl mt-3 leading-[1.05] text-brand-ink">
              Au-delà de l'IA, accompagnement global des organisations.
            </h2>
            <p className="mt-4 text-base md:text-lg text-slate-600 leading-relaxed">
              Le cabinet YAFORMIS opère également un second cabinet spécialisé en <strong className="text-brand-ink">marchés publics</strong>, <strong className="text-brand-ink">RSE</strong> et <strong className="text-brand-ink">réseaux sociaux</strong>. Un accompagnement complémentaire pour les entreprises qui veulent structurer leur croissance sur tous les fronts.
            </p>
            <a
              href="https://assistance-appel-offre.fr/"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-cta mt-7 inline-flex"
              data-testid="sister-site-cta"
            >
              Découvrir Assistance Appel d'Offre
              <ArrowUpRight className="w-5 h-5" />
            </a>
          </div>
          <div className="lg:col-span-7 grid grid-cols-1 md:grid-cols-3 gap-px bg-brand-line border border-brand-line">
            {expertises.map((e) => (
              <div
                key={e.title}
                className="bg-white p-6 md:p-7 hover:bg-brand-page transition-colors"
                data-testid={`sister-expertise-${e.title.toLowerCase().replace(/\s/g, '-')}`}
              >
                <e.icon className="w-7 h-7 text-brand-blue" strokeWidth={1.6} />
                <h3 className="font-heading font-bold text-lg mt-4 text-brand-ink">{e.title}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{e.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function BlogPreview() {
  const recent = blogPosts.slice(0, 3);
  return (
    <section className="bg-brand-surface py-14 md:py-20" data-testid="blog-preview-section">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
          <div>
            <div className="overline flex items-center gap-2"><Sparkles className="w-3.5 h-3.5" /> Ressources</div>
            <h2 className="font-heading font-black tracking-tighter text-3xl sm:text-4xl lg:text-5xl mt-3 leading-[1.05] text-brand-ink">
              Apprenez l'IA avec notre blog.
            </h2>
          </div>
          <Link to="/blog" className="btn-ghost text-sm" data-testid="blog-preview-cta">
            Tous les articles <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {recent.map((p) => (
            <Link
              key={p.slug}
              to={`/blog/${p.slug}`}
              data-testid={`blog-preview-${p.slug}`}
              className="group bg-white border border-brand-line hover:border-brand-ink transition-colors flex flex-col"
            >
              <div className="aspect-[16/10] overflow-hidden bg-[#0a1830]">
                <img src={p.cover} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              </div>
              <div className="p-6 flex-1 flex flex-col">
                <div className="flex items-center gap-3 text-xs text-slate-500">
                  <span className="font-semibold text-brand-blue uppercase tracking-wider">{p.category}</span>
                  <span>·</span>
                  <span>{p.readingTime}</span>
                </div>
                <h3 className="font-heading font-bold text-xl mt-3 leading-snug text-brand-ink group-hover:text-brand-blue transition-colors">
                  {p.title}
                </h3>
                <p className="mt-2 text-sm text-slate-600 line-clamp-2 flex-1">{p.excerpt}</p>
                <div className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-brand-ink group-hover:text-brand-blue">
                  Lire l'article <ArrowUpRight className="w-4 h-4" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
